import type { TokenCounter, TokenizerMode } from "@roqeet/core";
import { heuristicTokenCount } from "@roqeet/core";
// 타입만 import(빌드 시 erase). 실제 모듈/랭크는 lazy import로 가져온다(결정 #12:
// 정확 토크나이저는 6s+ 보호를 위해 lazy-load). 초기 번들에서 BPE 랭크가 빠진다.
import type { Tiktoken, TiktokenBPE, TiktokenEncoding } from "js-tiktoken/lite";

/**
 * 정확 토크나이저 — 결정 #12. core는 의존성 없는 휴리스틱(heuristicTokenCount)만
 * 두고, 정확한 BPE 카운트가 필요하면 web이 이 모듈로 주입한다.
 *
 * js-tiktoken(순수 JS, WASM 아님 — 6s+/Safari 15 호환)을 쓰며:
 *  - `lite` 클래스 + 모델별 BPE 랭크를 **서브패스로 분리 lazy import**한다.
 *    실제로 토큰 표시가 필요한 순간에만 내려받으므로 초기 로드가 가볍다.
 *  - 인코딩별 인코더를 캐시한다(같은 인코딩은 1벌만 구축).
 *  - 로드 실패 시 휴리스틱으로 우아하게 폴백한다(절대 throw 없음).
 *
 * 정확도 메모: OpenAI 계열은 공식 인코딩(cl100k/o200k)이라 정확하다. Claude/Gemini는
 * 공식 tiktoken 인코딩이 없어 cl100k_base를 근사로 쓴다(char/4 휴리스틱보다 정확).
 */

const encoderCache = new Map<TiktokenEncoding, Tiktoken>();
const loadingCache = new Map<TiktokenEncoding, Promise<Tiktoken | undefined>>();

/** 모델명 → tiktoken 인코딩 선택(휴리스틱 매칭). */
function encodingForModel(model: string): TiktokenEncoding {
  const m = model.toLowerCase();
  // GPT-4o / o1·o3 / gpt-4.1 계열은 o200k_base.
  if (
    m.includes("gpt-4o") ||
    m.includes("gpt-4.1") ||
    m.includes("gpt-5") ||
    m.includes("o200k") ||
    /(^|[^a-z])o[134]([^a-z]|$|-)/.test(m) // o1, o3, o4 (mini/preview 포함)
  ) {
    return "o200k_base";
  }
  // 그 외(gpt-4/gpt-3.5/claude/gemini/로컬)는 cl100k_base 근사.
  return "cl100k_base";
}

function encodingForOverride(mode: TokenizerMode | undefined): TiktokenEncoding | undefined {
  switch (mode) {
    case "cl100k_base":
    case "o200k_base":
    case "p50k_base":
    case "r50k_base":
    case "gpt2":
      return mode;
    default:
      return undefined;
  }
}

/** 인코딩별 BPE 랭크를 lazy import한다. 미지원 인코딩은 undefined. */
async function loadRanks(encoding: TiktokenEncoding): Promise<TiktokenBPE | undefined> {
  switch (encoding) {
    case "o200k_base":
      return (await import("js-tiktoken/ranks/o200k_base")).default;
    case "cl100k_base":
      return (await import("js-tiktoken/ranks/cl100k_base")).default;
    case "p50k_base":
      return (await import("js-tiktoken/ranks/p50k_base")).default;
    case "r50k_base":
      return (await import("js-tiktoken/ranks/r50k_base")).default;
    case "gpt2":
      return (await import("js-tiktoken/ranks/gpt2")).default;
    default:
      return undefined;
  }
}

/** 인코딩에 대한 Tiktoken 인코더를 lazy-load + 캐시한다. 실패 시 undefined. */
async function getEncoder(encoding: TiktokenEncoding): Promise<Tiktoken | undefined> {
  const cached = encoderCache.get(encoding);
  if (cached) return cached;
  let loading = loadingCache.get(encoding);
  if (!loading) {
    loading = (async () => {
      try {
        const [{ Tiktoken }, ranks] = await Promise.all([
          import("js-tiktoken/lite"),
          loadRanks(encoding),
        ]);
        if (!ranks) return undefined;
        const enc = new Tiktoken(ranks);
        encoderCache.set(encoding, enc);
        return enc;
      } catch {
        return undefined; // WASM/네트워크/파싱 실패 → 휴리스틱 폴백.
      }
    })();
    loadingCache.set(encoding, loading);
  }
  return loading;
}

/**
 * 모델에 맞는 **동기** TokenCounter를 준비한다(결정 #12). 인코더 로드는 비동기지만,
 * 반환된 카운터는 동기라 core 파이프라인(build-prompt 트림 / contextUsage)에 그대로
 * 주입할 수 있다. 로드 실패 시 휴리스틱 카운터를 돌려준다.
 */
export async function getExactTokenCounter(
  model: string,
  tokenizer?: TokenizerMode,
): Promise<TokenCounter> {
  if (tokenizer === "heuristic") return heuristicTokenCount;
  const encoding = encodingForOverride(tokenizer) ?? encodingForModel(model);
  const enc = await getEncoder(encoding);
  if (!enc) return heuristicTokenCount;
  return (text: string): number => {
    if (!text) return 0;
    try {
      // 특수 토큰을 일반 텍스트로 취급(allowedSpecial 미지정 시 throw 회피).
      return enc.encode(text, [], []).length;
    } catch {
      return heuristicTokenCount(text);
    }
  };
}

/** 단일 텍스트의 정확 토큰 수(편의 함수). 실패 시 휴리스틱. */
export async function countTokensExact(text: string, model: string): Promise<number> {
  const counter = await getExactTokenCounter(model);
  return counter(text);
}
